import { useEffect } from 'react';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { userKeys } from '@entities/User/queries';
import { realtimeManager } from '@services/websocket/RealtimeManager';

/**
 * Single owner of the socket's end of life: closes it once, when the app goes from
 * logged in to logged out. Mount it once, above every page (`_app`).
 *
 * It watches the query cache, not a `useCurrentUser` observer: `useLogout` navigates to
 * /login before `queryClient.clear()` (src/features/user/hooks/useAuth.ts), so by then no
 * `useRealtime` instance is mounted, and `clear()` does not notify observers anyway. The
 * cache still emits `removed` for the current-user query, which is the transition.
 * A refetch that fails keeps the previous user, so it is not taken for a logout.
 *
 * The backend does not close the socket at logout either (backend
 * src/modules/back/auth/controller.js:80-98); it only drops it when the token expires
 * (src/websocket/server.js:67-77). Left open, the socket stays in the previous user's
 * `user:<id>` room (src/websocket/server.js:102) and a later login in the same tab reuses it.
 */
export const useRealtimeSession = (): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const hasUser = () => queryClient.getQueryData(userKeys.me()) !== undefined;
    const currentUserHash = hashKey(userKeys.me());
    let wasLoggedIn = hasUser();

    return queryClient.getQueryCache().subscribe(event => {
      if (event.query.queryHash !== currentUserHash) return;

      const isLoggedIn = hasUser();
      if (wasLoggedIn && !isLoggedIn) realtimeManager.disconnect();
      wasLoggedIn = isLoggedIn;
    });
  }, [queryClient]);
};
